import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, 
  Star, 
  CheckCircle2, 
  XCircle,
  Building2,
  Calendar,
  Clock,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FeedbackForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [intervention, setIntervention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [feedback, setFeedback] = useState({
    rating: 0,
    effectiveness: 0,
    communication: 0,
    professionalism: 0,
    would_recommend: true,
    strengths: '',
    improvements: '',
    additional_comments: '',
    action: 'validate'
  });

  useEffect(() => {
    fetchIntervention();
  }, [id]);

  const fetchIntervention = async () => {
    try {
      const response = await axios.get(`${API}/interventions/${id}`);
      setIntervention(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'intervention');
      navigate('/pme/pending');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (action) => {
    if (feedback.rating === 0 || feedback.effectiveness === 0 || 
        feedback.communication === 0 || feedback.professionalism === 0) {
      toast.error('Veuillez attribuer toutes les notes');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/interventions/${id}/feedback`, {
        ...feedback,
        action
      });
      
      toast.success(action === 'validate' 
        ? 'Intervention validée avec succès !' 
        : 'Intervention rejetée'
      );
      navigate('/pme/pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du retour');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="space-y-2">
      <Label className="text-slate-700 font-medium">{label} *</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1 transition-transform hover:scale-110"
            data-testid={`star-${label.toLowerCase().replace(/\s+/g, '-')}-${star}`}
          >
            <Star 
              className={`w-8 h-8 ${star <= value 
                ? 'fill-orange-400 text-orange-400' 
                : 'text-slate-300 hover:text-orange-200'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  if (!intervention) return null;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 fade-in" data-testid="feedback-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/pme/pending')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            Donner votre avis
          </h1>
          <p className="text-slate-500 mt-1">
            Examinez et validez l'intervention
          </p>
        </div>

        {/* Intervention Summary */}
        <Card className="border-slate-200 shadow-sm bg-slate-50">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">{intervention.intervention_type}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">{intervention.consultant_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">{intervention.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">{intervention.duration_hours} heures</span>
              </div>
            </div>
            <Separator className="my-4" />
            <p className="text-sm text-slate-700">{intervention.description}</p>
          </CardContent>
        </Card>

        {/* Feedback Form */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-slate-900">Votre évaluation</CardTitle>
            <CardDescription>Évaluez la performance du consultant</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Ratings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <StarRating 
                label="Note globale" 
                value={feedback.rating}
                onChange={(value) => setFeedback({ ...feedback, rating: value })}
              />
              <StarRating 
                label="Efficacité" 
                value={feedback.effectiveness}
                onChange={(value) => setFeedback({ ...feedback, effectiveness: value })}
              />
              <StarRating 
                label="Communication" 
                value={feedback.communication}
                onChange={(value) => setFeedback({ ...feedback, communication: value })}
              />
              <StarRating 
                label="Professionnalisme" 
                value={feedback.professionalism}
                onChange={(value) => setFeedback({ ...feedback, professionalism: value })}
              />
            </div>

            <Separator />

            {/* Would Recommend */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-700 font-medium">Recommanderiez-vous ce consultant ?</Label>
                <p className="text-sm text-slate-500">Votre recommandation compte</p>
              </div>
              <Switch 
                checked={feedback.would_recommend}
                onCheckedChange={(checked) => setFeedback({ ...feedback, would_recommend: checked })}
                data-testid="would-recommend-switch"
              />
            </div>

            <Separator />

            {/* Text Feedback */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Points forts</Label>
                <Textarea
                  placeholder="Qu'est-ce que le consultant a bien fait ?"
                  value={feedback.strengths}
                  onChange={(e) => setFeedback({ ...feedback, strengths: e.target.value })}
                  rows={3}
                  data-testid="strengths-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Points à améliorer</Label>
                <Textarea
                  placeholder="Qu'est-ce qui pourrait être amélioré ?"
                  value={feedback.improvements}
                  onChange={(e) => setFeedback({ ...feedback, improvements: e.target.value })}
                  rows={3}
                  data-testid="improvements-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Commentaires additionnels</Label>
                <Textarea
                  placeholder="Autres remarques..."
                  value={feedback.additional_comments}
                  onChange={(e) => setFeedback({ ...feedback, additional_comments: e.target.value })}
                  rows={3}
                  data-testid="comments-input"
                />
              </div>
            </div>

            <Separator />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => handleSubmit('reject')}
                className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid="reject-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Rejeter l'intervention
              </Button>
              <Button
                type="button"
                disabled={submitting}
                onClick={() => handleSubmit('validate')}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="validate-btn"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Envoi...
                  </span>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Valider l'intervention
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
